import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Plus, RefreshCw, AlertTriangle, Beaker, Check, X, Droplet, Thermometer } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import {
  useGachees, useCreateGachee, useValiderGachee, useDeleteGachee,
} from '@/hooks/useBetonSprint2';
import { useOrdres } from '@/hooks/useBetonCatalog';
import { useFormulesBeton } from '@/hooks/useBetonCommandes';
import {
  STATUT_GACHEE_LABELS, STATUT_GACHEE_COLORS, type StatutGachee, type Gachee,
} from '@/types';
import { formatNumber } from '@/utils/currency';
import { extractError } from '@/api/client';
import { demander } from '@/components/ui/Fenetres';

const fmtDT = (iso: string) => new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
const TOLERANCE = 20;

export const GacheesPage = () => {
  const [statut, setStatut]         = useState<StatutGachee | ''>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading, refetch } = useGachees({ statut: statut || undefined, limit: 200 });
  const valider = useValiderGachee();
  const remove  = useDeleteGachee();
  const gachees = data?.data ?? [];

  const kpis = useMemo(() => ({
    brouillon:  gachees.filter((g) => g.statut === 'brouillon').length,
    valide:     gachees.filter((g) => g.statut === 'valide').length,
    horsNorme:  gachees.filter((g) => g.statut === 'hors_norme').length,
    volumeM3:   gachees.reduce((s, g) => s + parseFloat(g.volumeM3 || '0'), 0),
  }), [gachees]);

  const handle = async (fn: () => Promise<any>, confirmMsg?: string) => {
    if (confirmMsg && !(await demander.confirmer({ titre: confirmMsg, danger: true }))) return;
    setActionError(null);
    try { await fn(); }
    catch (e) { setActionError(extractError(e).message); }
  };

  return (
    <>
      <Topbar
        title="Gâchées béton"
        subtitle="Module 4 — suivi coulage (slump · T° · E/C) + traçabilité gâchée → lots"
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()} aria-label="Rafraîchir">
              <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
            </button>
            <button className="ds-btn ds-btn-p" onClick={() => setDialogOpen(true)}>
              <Plus size={14} /> Nouvelle gâchée
            </button>
          </>
        }
      />

      <div className="ds-content">
        {actionError && (
          <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">{actionError}</div>
          </div>
        )}

        <div className="ds-kpi-strip">
          <div className="ds-ks ks-orange">
            <div className="ds-ks-label">Brouillons</div>
            <div className="ds-ks-value">{kpis.brouillon}</div>
            <div className="ds-ks-sub">En attente validation</div>
          </div>
          <div className="ds-ks ks-green">
            <div className="ds-ks-label">Validées</div>
            <div className="ds-ks-value">{kpis.valide}</div>
            <div className="ds-ks-sub">Alimentent lots</div>
          </div>
          <div className="ds-ks ks-red">
            <div className="ds-ks-label">Hors norme</div>
            <div className="ds-ks-value">{kpis.horsNorme}</div>
            <div className="ds-ks-sub">Slump hors ±{TOLERANCE}mm</div>
          </div>
          <div className="ds-ks ks-blue">
            <div className="ds-ks-label">Volume coulé</div>
            <div className="ds-ks-value">{formatNumber(kpis.volumeM3)}</div>
            <div className="ds-ks-sub">m³ (filtre actif)</div>
          </div>
        </div>

        <div className="ds-filters">
          <select className="ds-fi" value={statut} onChange={(e) => setStatut(e.target.value as StatutGachee | '')} style={{ width: 'auto' }}>
            <option value="">Tous statuts</option>
            {(Object.keys(STATUT_GACHEE_LABELS) as StatutGachee[]).map((s) => (
              <option key={s} value={s}>{STATUT_GACHEE_LABELS[s]}</option>
            ))}
          </select>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header">
            <div>
              <div className="ds-table-title">Gâchées</div>
              <div className="ds-table-sub">{gachees.length} gâchée{gachees.length > 1 ? 's' : ''} · Sprint 2 Module 4</div>
            </div>
          </div>
          <table className="ds-tbl">
            <thead>
              <tr>
                <th style={{ width: 130 }}>Référence</th>
                <th style={{ width: 130 }}>OF · Formule</th>
                <th>Coulage</th>
                <th className="ds-am" style={{ width: 90 }}>Volume</th>
                <th style={{ width: 110 }}>Slump</th>
                <th style={{ width: 90 }}>E/C</th>
                <th style={{ width: 90 }}>Statut</th>
                <th style={{ width: 90 }}>Lots</th>
                <th style={{ width: 150 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 32, color: 'var(--tx-3)' }}>Chargement…</td></tr>
              )}
              {!isLoading && gachees.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: 48 }}>
                    <Beaker size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                    <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucune gâchée enregistrée</p>
                    <button className="ds-btn ds-btn-p ds-btn-sm" style={{ marginTop: 12 }} onClick={() => setDialogOpen(true)}>
                      <Plus size={12} /> Saisir la première gâchée
                    </button>
                  </td>
                </tr>
              )}
              {!isLoading && gachees.map((g) => (
                <GacheeRow key={g.id} g={g}
                  onValider={() => handle(() => valider.mutateAsync(g.id), `Valider ${g.reference} ?`)}
                  onDelete ={() => handle(() => remove.mutateAsync(g.id),  `Supprimer ${g.reference} définitivement ?`)}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <GacheeFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </>
  );
};

// ── Ligne tableau ───────────────────────────────────────────────────────

const GacheeRow = ({ g, onValider, onDelete }: { g: Gachee; onValider: () => void; onDelete: () => void }) => {
  const slumpEcart = g.slumpMm != null && g.slumpCibleMm != null
    ? Math.abs(g.slumpMm - g.slumpCibleMm) : null;
  const slumpHorsNorme = slumpEcart != null && slumpEcart > TOLERANCE;
  return (
    <tr>
      <td><span className="ds-ref-code">{g.reference}</span></td>
      <td>
        <div className="ds-entry-label">{g.ordre?.reference ?? '—'}</div>
        <div className="ds-entry-sub">{g.formuleBeton?.code ?? '—'}</div>
      </td>
      <td style={{ fontSize: 11, color: 'var(--tx-2)' }}>
        {fmtDT(g.heureCoulage)}
        {g.centrale && <div style={{ fontSize: 10, color: 'var(--tx-3)' }}>{g.centrale}</div>}
        {(g.tempEauC || g.tempAmbianteC) && (
          <div style={{ fontSize: 10, color: 'var(--tx-3)', display: 'flex', alignItems: 'center', gap: 3 }}>
            <Thermometer size={9} />
            {g.tempEauC && `eau ${g.tempEauC}°`}
            {g.tempAmbianteC && ` · amb ${g.tempAmbianteC}°`}
          </div>
        )}
      </td>
      <td className="ds-am">{formatNumber(parseFloat(g.volumeM3))} m³</td>
      <td>
        {g.slumpMm != null ? (
          <div style={{ fontSize: 11, color: slumpHorsNorme ? 'var(--warning)' : 'var(--tx-2)', fontWeight: slumpHorsNorme ? 600 : 400 }}>
            {slumpHorsNorme && <AlertTriangle size={10} style={{ display: 'inline', marginRight: 3 }} />}
            {g.slumpMm}mm
            {g.slumpCibleMm != null && <span style={{ color: 'var(--tx-3)', fontSize: 10 }}> /{g.slumpCibleMm}</span>}
          </div>
        ) : (
          <span style={{ color: 'var(--tx-3)' }}>—</span>
        )}
      </td>
      <td style={{ fontSize: 11 }}>{g.rapportEC ?? '—'}</td>
      <td>
        <span className="ds-bd" style={{ background: STATUT_GACHEE_COLORS[g.statut], color: 'white', fontSize: 10 }}>
          {STATUT_GACHEE_LABELS[g.statut]}
        </span>
      </td>
      <td style={{ fontSize: 11 }}>{g.lots?.length ?? 0}</td>
      <td>
        <div style={{ display: 'flex', gap: 4 }}>
          {g.statut !== 'valide' && (
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={onValider} title="Valider" style={{ color: 'var(--success)' }}>
              <Check size={11} />
            </button>
          )}
          {g.statut !== 'valide' && (
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={onDelete} title="Supprimer" style={{ color: 'var(--danger)' }}>
              <X size={11} />
            </button>
          )}
          {g.statut === 'valide' && <span style={{ fontSize: 10, color: 'var(--tx-3)' }}>Verrouillée</span>}
        </div>
      </td>
    </tr>
  );
};

// ── Dialog création ─────────────────────────────────────────────────────

const GacheeFormDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const [ordreId, setOrdreId]               = useState('');
  const [formuleBetonId, setFormuleId]      = useState('');
  const [centrale, setCentrale]             = useState('');
  const [heureCoulage, setHeureCoulage]     = useState(new Date().toISOString().slice(0, 16));
  const [volumeM3, setVolumeM3]             = useState('');
  const [slumpMm, setSlumpMm]               = useState('');
  const [slumpCibleMm, setSlumpCible]       = useState('80');
  const [tempEauC, setTempEau]              = useState('');
  const [tempAmbianteC, setTempAmb]         = useState('');
  const [rapportEC, setRapportEC]           = useState('');
  const [notes, setNotes]                   = useState('');
  const [error, setError]                   = useState<string | null>(null);

  const { data: ordresData }   = useOrdres({ statut: 'en_cours', limit: 100 });
  const { data: formulesData } = useFormulesBeton();
  const create = useCreateGachee();
  const ordres   = ordresData?.data ?? [];
  const formules = formulesData ?? [];

  useEffect(() => {
    if (open) {
      setOrdreId(''); setFormuleId(''); setCentrale(''); setVolumeM3(''); setSlumpMm('');
      setSlumpCible('80'); setTempEau(''); setTempAmb(''); setRapportEC(''); setNotes(''); setError(null);
      setHeureCoulage(new Date().toISOString().slice(0, 16));
    }
  }, [open]);

  const slumpNum  = slumpMm     ? parseInt(slumpMm, 10) : null;
  const cibleNum  = slumpCibleMm ? parseInt(slumpCibleMm, 10) : null;
  const slumpEcart = slumpNum != null && cibleNum != null ? Math.abs(slumpNum - cibleNum) : null;
  const slumpHorsNorme = slumpEcart != null && slumpEcart > TOLERANCE;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({
        ordreId,
        formuleBetonId: formuleBetonId || undefined,
        centrale:       centrale.trim() || undefined,
        heureCoulage:   new Date(heureCoulage).toISOString(),
        volumeM3:       parseFloat(volumeM3),
        slumpMm:        slumpMm ? parseInt(slumpMm, 10) : undefined,
        slumpCibleMm:   slumpCibleMm ? parseInt(slumpCibleMm, 10) : undefined,
        tempEauC:       tempEauC ? parseFloat(tempEauC) : undefined,
        tempAmbianteC:  tempAmbianteC ? parseFloat(tempAmbianteC) : undefined,
        rapportEC:      rapportEC ? parseFloat(rapportEC) : undefined,
        notes:          notes.trim() || undefined,
      });
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  const canSubmit = ordreId && parseFloat(volumeM3 || '0') > 0;

  return (
    <Dialog open={open} onClose={onClose} title="Nouvelle gâchée" subtitle="Réf auto G-YYYY-NNNNN · statut auto selon slump ±20mm" size="lg">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}

        <Field label="Ordre de fabrication (en cours) *">
          {ordres.length === 0 ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
              Aucun OF en cours. Démarrez un OF d'abord (page Production).
            </div>
          ) : (
            <select value={ordreId} onChange={(e) => setOrdreId(e.target.value)}
              className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm" required>
              <option value="">— Sélectionner —</option>
              {ordres.map((of) => (
                <option key={of.id} value={of.id}>{of.reference} · {of.produit?.designation}</option>
              ))}
            </select>
          )}
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Formule béton (recommandée pour QC)">
            <select value={formuleBetonId} onChange={(e) => setFormuleId(e.target.value)}
              className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm">
              <option value="">— Aucune —</option>
              {formules.filter((f) => f.actif).map((f) => (
                <option key={f.id} value={f.id}>{f.code} · {f.libelle}</option>
              ))}
            </select>
          </Field>
          <Field label="Centrale">
            <input value={centrale} onChange={(e) => setCentrale(e.target.value)}
              className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm" placeholder="Centrale 1" />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Heure de coulage *">
            <input type="datetime-local" value={heureCoulage} onChange={(e) => setHeureCoulage(e.target.value)}
              className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm" required />
          </Field>
          <Field label="Volume coulé (m³) *">
            <input type="number" min="0.01" step="0.01" value={volumeM3} onChange={(e) => setVolumeM3(e.target.value)}
              className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm" required placeholder="1.5" />
          </Field>
        </div>

        <div className="rounded-md border border-blue-200 bg-blue-50/40 p-3 space-y-3">
          <div className="text-xs font-semibold text-blue-900 flex items-center gap-1">
            <Droplet size={11} /> Contrôle béton frais
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Slump mesuré (mm)">
              <input type="number" min="0" max="300" step="1" value={slumpMm} onChange={(e) => setSlumpMm(e.target.value)}
                className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm" placeholder="85" />
            </Field>
            <Field label="Slump cible (mm)">
              <input type="number" min="0" max="300" step="1" value={slumpCibleMm} onChange={(e) => setSlumpCible(e.target.value)}
                className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm" placeholder="80" />
            </Field>
            <Field label="Rapport E/C">
              <input type="number" min="0" max="2" step="0.01" value={rapportEC} onChange={(e) => setRapportEC(e.target.value)}
                className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm" placeholder="0.5" />
            </Field>
          </div>
          {slumpEcart != null && (
            <div className={`text-xs ${slumpHorsNorme ? 'text-amber-700' : 'text-emerald-700'}`}>
              {slumpHorsNorme ? '⚠' : '✓'} Écart {slumpEcart}mm — tolérance ±{TOLERANCE}mm{slumpHorsNorme ? ' DÉPASSÉE → statut "hors_norme" auto' : ''}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="T° eau (°C)">
              <input type="number" min="0" max="50" step="0.1" value={tempEauC} onChange={(e) => setTempEau(e.target.value)}
                className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm" placeholder="22.5" />
            </Field>
            <Field label="T° ambiante (°C)">
              <input type="number" min="0" max="50" step="0.1" value={tempAmbianteC} onChange={(e) => setTempAmb(e.target.value)}
                className="flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 text-sm" placeholder="31" />
            </Field>
          </div>
        </div>

        <Field label="Notes (optionnel)">
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
            className="flex w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
            placeholder="Conditions météo, incident, observations…" />
        </Field>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!canSubmit || create.isPending}
            style={{ opacity: !canSubmit || create.isPending ? 0.5 : 1 }}>
            <Plus size={14} /> {create.isPending ? 'Création…' : 'Créer gâchée'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
    {children}
  </div>
);
